import { API_URL } from "./constants";

const getToken = () => localStorage.getItem('access_token');

/**
 * The branch every request is scoped to.
 *
 * Read from localStorage rather than from BranchContext so that provider order
 * is irrelevant and the multipart helpers below can reuse it unchanged.
 */
const getBranch = () => localStorage.getItem('branch_scope') === 'all'
  ? 'all'
  : localStorage.getItem('active_branch_id');

const buildHeaders = (extra = {}) => {
  const token = getToken();
  const branch = getBranch();

  return {
    'Accept': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    ...(branch ? { 'X-Branch-Id': branch } : {}),
    ...extra,
  };
};

const clearSession = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('user');
  localStorage.removeItem('permissions');
  localStorage.removeItem('active_branch_id');
  localStorage.removeItem('branches');
  localStorage.removeItem('branch_scope');
};

/**
 * Marks that a reload has already been spent trying to clear a branch problem.
 *
 * sessionStorage rather than a module variable, because the reload it guards
 * discards the module. Cleared on the first response that comes back fine.
 */
const RETRY_FLAG = 'branch_recovery_attempted';

/** Read once by the login screen, which owns the only guaranteed toast host. */
const SESSION_ERROR_KEY = 'session_error';

/**
 * End the session on a fault no reload can clear, leaving the login screen a
 * message to show.
 */
const endSessionWith = (messageKey) => {
  clearSession();
  sessionStorage.removeItem(RETRY_FLAG);
  localStorage.setItem(SESSION_ERROR_KEY, messageKey);
  window.location.href = '/';
};

/**
 * Handle the two failures every request shares.
 *
 * Checks the status rather than a thrown error's message: the previous version
 * inspected error.message inside a catch that only fires on network or JSON
 * failures, so an API 401 body never reached it and the redirect never ran.
 *
 * Returns true when the caller should stop, because a redirect or reload is
 * already under way.
 */
const handleSessionFailures = async (response) => {
  if (response.ok) {
    sessionStorage.removeItem(RETRY_FLAG);
    return false;
  }

  if (response.status === 401) {
    clearSession();
    window.location.href = '/';
    return true;
  }

  if (response.status === 403 || response.status === 409) {
    const body = await response.clone().json().catch(() => ({}));

    /**
     * The account reaches no branch at all. Dropping the stored branch cannot
     * help — there is nothing to re-resolve to — so this goes straight to the
     * login screen. Reloading instead put the app in a refresh loop: every
     * reload remounted BranchProvider, which fetched, which answered 409 again.
     */
    if (body.code === 'no_branch_available') {
      endSessionWith('errors.noBranchAvailable');
      return true;
    }

    // The stored branch is stale (access revoked, branch deactivated). Drop it
    // and let the app re-resolve rather than leaving every screen failing.
    if (body.code === 'branch_not_accessible' || body.code === 'branch_scope_forbidden') {
      localStorage.removeItem('active_branch_id');
      localStorage.removeItem('branch_scope');

      // One reload is a fix; a second means the server is rejecting whatever the
      // app resolves to, and reloading again would only spin.
      if (sessionStorage.getItem(RETRY_FLAG)) {
        endSessionWith('errors.branchUnavailable');
        return true;
      }

      sessionStorage.setItem(RETRY_FLAG, '1');
      window.location.reload();
      return true;
    }
  }

  return false;
};

const request = async (endpoint, method = 'GET', data = null) => {
  const config = {
    method,
    headers: buildHeaders({ 'Content-Type': 'application/json' }),
  };

  if (data) {
    config.body = JSON.stringify(data);
  }

  try {
    const response = await fetch(`${API_URL}${endpoint}`, config);

    if (await handleSessionFailures(response)) {
      return null;
    }

    return await response.json();
  } catch (error) {
    return null;
  }
};

/**
 * Multipart uploads.
 *
 * Content-Type is deliberately omitted: the browser has to set it itself so it
 * can append the multipart boundary.
 */
export const postForm = async (endpoint, formData, method = 'POST') => {
  try {
    const response = await fetch(`${API_URL}${endpoint}`, {
      method,
      headers: buildHeaders(),
      body: formData,
    });

    if (await handleSessionFailures(response)) {
      return null;
    }

    return await response.json();
  } catch (error) {
    return null;
  }
};

/** File downloads (exports), which return a blob rather than JSON. */
export const getBlob = async (endpoint) => {
  try {
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'GET',
      headers: buildHeaders(),
    });

    if (await handleSessionFailures(response)) {
      return null;
    }

    return await response.blob();
  } catch (error) {
    return null;
  }
};

export default request;
